import React from 'react';
import { CheckCircle, XCircle, Clock, Loader2, Database, Cpu, FileText, Download } from 'lucide-react';
import { ProgressInfo } from '../utils/api';

interface GenerationProgress {
    stage: string;
    status: string;
    message: string;
    timestamp: string;
    data?: any;
}

interface ProgressIndicatorProps {
    progress: GenerationProgress[] | ProgressInfo | null;
    isGenerating: boolean;
    error?: string;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
    progress,
    isGenerating,
    error
}) => {
    // 阶段配置
    const stageConfig = {
        intent: { icon: Cpu, label: '意图识别', color: 'text-blue-600' },
        context: { icon: Database, label: '任务准备', color: 'text-blue-600' },
        pipeline: { icon: FileText, label: '流程初始化', color: 'text-blue-600' },
        fetch_clean: { icon: Download, label: '数据获取', color: 'text-green-600' },
        process: { icon: Cpu, label: '数据处理', color: 'text-yellow-600' },
        generate: { icon: FileText, label: '报告生成', color: 'text-purple-600' },
        merge: { icon: FileText, label: '报告合并', color: 'text-purple-600' },
        complete: { icon: CheckCircle, label: '完成', color: 'text-green-600' },
        error: { icon: XCircle, label: '错误', color: 'text-red-600' }
    };

    // 如果没有进度数据，显示默认的处理步骤
    const defaultProgress = [
        { stage: 'intent', status: 'pending', message: '准备解析查询意图...', timestamp: new Date().toISOString() },
        { stage: 'pipeline', status: 'pending', message: '准备初始化处理流程...', timestamp: new Date().toISOString() },
        { stage: 'complete', status: 'pending', message: '等待报告生成完成...', timestamp: new Date().toISOString() },
    ];

    // 处理不同类型的 progress
    let displayProgress: GenerationProgress[] = defaultProgress;
    if (Array.isArray(progress) && progress.length > 0) {
        displayProgress = progress;
    } else if (progress && typeof progress === 'object' && 'stage' in progress) {
        // 单个进度对象，转换为数组格式
        displayProgress = [{
            stage: progress.stage,
            status: progress.status,
            message: progress.message,
            timestamp: new Date().toISOString()
        }];
    }

    const getStageStatus = (stage: string, status: string) => {
        if (status === 'completed') return 'completed';
        if (status === 'processing') return 'processing';
        if (status === 'error') return 'error';
        return 'pending';
    };

    const getProgressStatus = (item: GenerationProgress, index: number) => {
        // 根据当前生成状态和进度位置判断状态
        if (!isGenerating && !progress) return 'pending';
        if (item.status !== 'pending') return getStageStatus(item.stage, item.status);

        // 如果正在生成，根据索引判断状态
        if (isGenerating) {
            const currentIndex = displayProgress.length - 1;
            if (index < currentIndex) return 'completed';
            if (index === currentIndex) return 'processing';
        }
        return 'pending';
    };

    const getStatusIcon = (stage: string, status: string) => {
        const stageStatus = getStageStatus(stage, status);

        switch (stageStatus) {
            case 'completed':
                return <CheckCircle className="h-5 w-5 text-green-600" />;
            case 'processing':
                return <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />;
            case 'error':
                return <XCircle className="h-5 w-5 text-red-600" />;
            default:
                return <Clock className="h-5 w-5 text-gray-400" />;
        }
    };

    const getStatusColor = (stage: string, status: string) => {
        const stageStatus = getStageStatus(stage, status);
        switch (stageStatus) {
            case 'completed':
                return 'border-green-200 bg-green-50';
            case 'processing':
                return 'border-blue-200 bg-blue-50 animate-pulse';
            case 'error':
                return 'border-red-200 bg-red-50';
            default:
                return 'border-gray-200 bg-gray-50';
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            <div className="card">
                <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        {isGenerating ? '报告生成中...' : '处理完成'}
                    </h2>
                    <p className="text-gray-600">
                        {isGenerating
                            ? '正在为您生成专业的金融分析报告，请稍候...'
                            : '报告生成完成，您可以查看或下载结果'
                        }
                    </p>
                </div>

                {/* 进度步骤 */}
                <div className="space-y-4">
                    {displayProgress.map((item, index) => {
                        const stageKey = item.stage as keyof typeof stageConfig;
                        const stageInfo = stageConfig[stageKey];
                        const currentStatus = getProgressStatus(item, index);

                        return (
                            <div
                                key={index}
                                className={`flex items-center p-4 rounded-lg border-2 transition-all duration-300 ${getStatusColor(item.stage, currentStatus)}`}
                            >
                                <div className="flex-shrink-0 mr-4">
                                    {getStatusIcon(item.stage, currentStatus)}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-medium text-gray-900">
                                            {stageInfo?.label || item.stage}
                                        </h3>
                                        <span className="text-sm text-gray-500">
                                            {item.timestamp ? new Date(item.timestamp).toLocaleTimeString() : ''}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {item.message}
                                    </p>
                                    {(item as any).data && (
                                        <div className="mt-2 text-xs text-gray-500">
                                            {typeof (item as any).data === 'string' ? (item as any).data : JSON.stringify((item as any).data, null, 2)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* 错误显示 */}
                {error && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                            <XCircle className="h-5 w-5 text-red-600 mr-3" />
                            <div>
                                <h3 className="font-medium text-red-900">生成失败</h3>
                                <p className="text-sm text-red-700 mt-1">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* 加载动画 */}
                {isGenerating && (
                    <div className="mt-6 text-center">
                        <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <Loader2 className="h-5 w-5 text-blue-600 animate-spin mr-2" />
                            <span className="text-blue-700">正在处理中，请稍候...</span>
                        </div>
                    </div>
                )}
            </div>

            {/* 处理提示 */}
            <div className="mt-6 text-center text-sm text-gray-500">
                <p>生成过程可能需要几分钟时间，请耐心等待</p>
                <p>报告生成完成后，您可以下载Markdown格式的文件</p>
            </div>
        </div>
    );
};

export default ProgressIndicator;
